import './app.scss';

import domready from 'domready';
import flatpickr from 'flatpickr';
import { German } from 'flatpickr/dist/l10n/de.js';
import EventScroller from './utilities/js/event-scroller';
import { BackToTop } from './areas/back-to-top/back-to-top';
import { ResizeHandler } from './utilities/js/resize-handler';
import { MediaMatchHandler } from './utilities/js/media-match-handler';
import Lazysizes from 'Lazysizes';
import Search from './components/search/search';
import Overlay from './components/overlay/overlay';
import { initScrollToAnchor } from './utilities/js/scroll-to-anchor';
import { FormHandler } from './components/form-handler/form-handler';
import { Scrolltable } from './components/scrolltable/scrolltable';

// GLOBAL OPTIONS
window.OPTIONS = { // eslint-disable-line
    breakpoints: {
        s: 320,
        m: 768,
        l: 1024,
        xl: 1280,
        xxl: 1440,
        xxxl: 1920
    },
    loader: '<b class="loading"><b class="loading__loader"><i>loading...</i></b></b>'
};

const DEBUG = false;
let userActivatedPauseOnMoodVideo = false;

window.mediaMatchHandler = new MediaMatchHandler();
window.youtubeApi = false;
window.youtubeApiLoading = false;
window.resizeHandler = new ResizeHandler();

window.cookieConsentInitialized = false;

const browserCheck = () => {
    window.browser = {
        csshover: !window.matchMedia('(pointer:coarse)').matches,
        windows: (/windows/i).test(navigator.userAgent),
        ios: (/iPhone|iPad|iPod/i).test(navigator.userAgent),
        iphone: (/iPhone|iPod/i).test(navigator.userAgent),
        android: (/(android)/i).test(navigator.userAgent),
        firefox: (/firefox/i).test(navigator.userAgent),
        edge: (/Edg\//i).test(navigator.userAgent),
        safari: (/Version\/[\d.]+.*Safari/).test(navigator.userAgent)
    };

    Object.entries(window.browser).forEach(([key, value]) => {
        document.documentElement.classList.add((!value) ? `no-${key}` : `${key}`);
    });
};

const waitForConsent = setInterval(() => {
    if (typeof CookieConsent !== 'undefined') {
        window.cookieConsentInitialized = true;
        clearInterval(waitForConsent);
    }
}, 300);

window._log = function (output) {
    if (typeof DEBUG !== 'undefined' && DEBUG !== false) {
        console.log(output);
    }
};

window._uniqueID = () => {

    const chr4 = () => {
        return Math.random().toString(16).slice(-4);
    };

    return chr4() + chr4() +
        '-' + chr4() +
        '-' + chr4() +
        '-' + chr4() +
        '-' + chr4() + chr4() + chr4();
};

window._createElementFromHTML = (htmlString) => {

    let div = document.createElement('div');
    div.innerHTML = htmlString.trim();

    if (div.children.length > 1) {
        let itemsArray = [];
        Array.prototype.push.apply(itemsArray, div.children);

        return itemsArray;
    } else {
        return div.firstChild;
    }
};

// Global input modality tracking (keyboard vs pointer)
// Exposes window.inputModality and window.setupInputModalityTracking so that
// any component can rely on a single, shared source of truth.
if (!window.inputModality) {
    window.inputModality = {setup: false, isKeyboard: false};
}

window.setupInputModalityTracking = () => {
    const modality = window.inputModality;
    if (!modality || modality.setup) return;
    modality.setup = true;

    const setKeyboard = (e) => {
        // Ignore modifier-only presses
        if (e && (e.altKey || e.ctrlKey || e.metaKey)) return;
        modality.isKeyboard = true;
    };

    const setPointer = () => {
        modality.isKeyboard = false;
    };

    // Key presses imply keyboard navigation (Tab/Enter/Space etc.)
    window.addEventListener('keydown', setKeyboard, {passive: true});

    // Any pointer/touch interaction implies non-keyboard modality
    window.addEventListener('pointerdown', setPointer, {passive: true});
    window.addEventListener('mousedown', setPointer, {passive: true});
    window.addEventListener('touchstart', setPointer, {passive: true});
};

const initContent = ($content) => {

    _log('start app');
    _log(Lazysizes, DEBUG);

    if (!document.querySelector('body').classList.contains('is--editmode')) {        
        const $$scrollTables = $content.querySelectorAll('table');

        $$scrollTables.forEach($scrollTable => {
            new Scrolltable($scrollTable);
        });
    }

    const $selects = $content.querySelectorAll('select:not([no-uniform])');
    for (let i = 0; i < $selects.length; i++) {
        const $select = $selects[i];
        const $parentSelect = $select.parentNode;

        if (!$parentSelect.classList.contains('select')) {
            const $selectWrapper = document.createElement('div');
            $selectWrapper.classList.add('select');

            $parentSelect.insertBefore($selectWrapper, $select);

            $selectWrapper.appendChild($select);
        }
    }

    const $overlays = $content.querySelectorAll('[data-overlay="root"]');
    if ($overlays.length > 0) {

        for (let i = 0; i < $overlays.length; i++) {
            let $overlay = $overlays[i];

            new Overlay($overlay, {
                loader: window.OPTIONS.loader,
                onLoaded: ($overlay) => {
                    initContent($overlay);
                }
            });
        }
    }

    const $$forms = $content.querySelectorAll('[data-form="root"]');

    $$forms.forEach($form => {
        new FormHandler($form, {
            loader: window.OPTIONS.loader,
            onSubmitted: ($form) => {
                initContent($form);
            }
        });
    });

    const $dateInputs = $content.querySelectorAll('input[type="date"]');
    for (let i = 0; i < $dateInputs.length; i++) {
        const $dateInput = $dateInputs[i];
        let maxDate = null;

        if ($dateInput.getAttribute('max') !== null) {
            maxDate = $dateInput.getAttribute('max');
        }

        const datepicker = flatpickr($dateInput, {
            altFormat: 'd.m.Y',
            altInput: true,
            allowInput: true,
            disableMobile: false,
            locale: German,
            maxDate: maxDate
        });

        const $altDate = $dateInput.nextElementSibling;
        if ($altDate.classList.contains('flatpickr-input')) {
            $altDate.addEventListener('blur', () => {
                let altDate = $altDate.value.split('.');

                if (altDate.length === 3) {
                    const altDateDay = altDate[0],
                        altDateMonth = altDate[1],
                        altDateYear = altDate[2];

                    if (altDateDay.length === 2 && altDateMonth.length === 2 && altDateYear.length === 4) {
                        const newDate = altDateYear + '-' + altDateMonth + '-' + altDateDay;
                        datepicker.setDate(newDate, true);
                    }
                }
            });
        }
    }

    if (typeof Modernizr !== 'undefined') {

        if (!Modernizr.csshover) {

            const $hovers = $content.querySelectorAll('[data-hover]');
            if ($hovers.length > 0) {

                for (let i = 0; i < $hovers.length; i++) {
                    let $hover = $hovers[i];
                    let hoverClass = $hover.getAttribute('data-hover');

                    if (hoverClass === '') {
                        hoverClass = 'hover';
                    }

                    $hover.addEventListener('mouseover', () => {

                        if ($hover.classList.contains(hoverClass)) {
                            $hover.classList.remove(hoverClass);
                        } else {
                            $hover.classList.add(hoverClass);
                        }
                    });

                    document.addEventListener('touchstart', (e) => {
                        let isClickInside = $hover.contains(e.target);

                        if (!isClickInside) {
                            $hover.classList.remove(hoverClass);
                        }
                    });
                }
            }
        }
    }

    const $backToTop = document.querySelector('[data-back-to-top]');
    if ($backToTop) {
        const backToTop = new BackToTop($backToTop);
        backToTop.init();
    }

    const $$anchors = $content.querySelectorAll('a[href*="#"]');
    if ($$anchors.length > 0) {
        const anchorSearchList = generateFalseSlugByText();
        const falseAnchors = Object.keys(anchorSearchList);

        for (let i = 0; i < $$anchors.length; i++) {
            const $anchor = $$anchors[i];
            let newUrl = '';
            const baseUrl = $anchor.getAttribute('href').split('#')[0];
            let targetId = $anchor.getAttribute('href').split('#')[1].toLowerCase();
            targetId = encodeURI(targetId);

            if (falseAnchors.includes(targetId)) {
                newUrl = baseUrl + '#' + anchorSearchList[targetId];
            } else {
                newUrl = baseUrl + '#' + targetId.toLowerCase();
            }

            $anchor.setAttribute('href', newUrl);
        }
    }
};

window.checkInview = () => {
    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                entry.target.classList.add('inview');
                observer.unobserve(entry.target);
            }
        });
    }, { threshold: 0, rootMargin: '0px 100% -10% 100%' });

    const $inviews = document.querySelectorAll('[data-inview]:not(.inview)');
    $inviews.forEach((target) => {
        observer.observe(target);
    });
};

const playMoodVideo = ($video, $playBtn, $pauseBtn) => {
    $video.play();
    $video.setAttribute('playing', true);
    $playBtn.setAttribute('aria-pressed', 'true');
    $pauseBtn.setAttribute('aria-pressed', 'false');
};

const pauseMoodVideo = ($video, $playBtn, $pauseBtn) => {
    $video.pause();
    $video.removeAttribute('playing');
    $pauseBtn.setAttribute('aria-pressed', 'true');
    $playBtn.setAttribute('aria-pressed', 'false');
};

const checkMoodVideo = (scroller) => {
    const $videos = document.querySelectorAll('[data-moodvideo]');

    $videos.forEach(($video) => {
        let onScreenStatus = scroller.onScreen($video);
        const $playBtn = $video.parentElement.querySelector('[data-moodvideo-play]');
        const $pauseBtn = $video.parentElement.querySelector('[data-moodvideo-pause]');

        $playBtn.onclick = () => {
            userActivatedPauseOnMoodVideo = false;
            playMoodVideo($video, $playBtn, $pauseBtn);
            $pauseBtn.focus();
        };

        $pauseBtn.onclick = () => {
            userActivatedPauseOnMoodVideo = true;
            pauseMoodVideo($video, $playBtn, $pauseBtn);
            $playBtn.focus();
        };

        if (onScreenStatus === true && userActivatedPauseOnMoodVideo === false) {
            playMoodVideo($video, $playBtn, $pauseBtn);
        } else if ($video.paused !== true) {
            pauseMoodVideo($video, $playBtn, $pauseBtn);
        }
    });
};

const checkBackToTopButton = () => {
    const position = window.pageYOffset;
    const $backToTop = document.querySelector('[data-back-to-top]');
    const visibilityOffset = document.body.scrollHeight / 2 - window.innerHeight / 2;

    if (position >= visibilityOffset) {
        $backToTop.classList.add('back-to-top--visible');
    } else {
        $backToTop.classList.remove('back-to-top--visible');
    }
};

/* this function generates dynamically false slugs from page anchors */
const generateFalseSlugByText = () => {
    let incorrectPageSlugs = {};
    const $$anchorObjects = document.querySelectorAll('.anchor');
    $$anchorObjects.forEach(($anchor) => {
        const anchorText = $anchor.innerText;
        const anchorId = $anchor.getAttribute('id');

        if (anchorId) {
            const falseSlug = encodeURI(anchorText.toLowerCase());
            const incorrectSlug = {
                [falseSlug]: anchorId,
            };

            incorrectPageSlugs = Object.assign(incorrectPageSlugs, incorrectSlug);
        }
    });
    return incorrectPageSlugs;
};

/* this function redirects false anchors given by location.hash to correct anchor */
const redirectAnchors = (callback) => {
    const anchorSearchList = generateFalseSlugByText();
    const falseAnchors = Object.keys(anchorSearchList);
    const locationHash = window.location.hash;
    let testAnchor = null;

    if (locationHash) {
        testAnchor = locationHash.split('#')[1];
    }

    if (falseAnchors.includes(testAnchor)) {
        window.location.hash = anchorSearchList[testAnchor];
    }

    if (typeof callback === 'function') {
        callback();
    }
};

/* this is a global function to trigger checkboxes like a label */
const initDataForCheckboxes = ($content) => {
    const $$checkboxTrigger = $content.querySelectorAll('[data-for-checkbox]');

    const resetAllTriggersByReference = (reference, state) => {
        const $allReferenceCheckboxes = $content.querySelectorAll(`[data-for-checkbox="${reference}"]`); // eslint-disable-line
        const $checkbox = $content.querySelector(`#${reference}`); // eslint-disable-line

        if ($checkbox) {
            $checkbox.checked = false;
        }

        if ($allReferenceCheckboxes.length > 0) {
            $allReferenceCheckboxes.forEach($checkboxTrigger => {
                let triggerActiveAttribute = 'aria-pressed';

                if ($checkboxTrigger.dataset.activeAttribute !== undefined) {
                    triggerActiveAttribute = $checkboxTrigger.dataset.activeAttribute;
                }

                $checkboxTrigger.setAttribute(triggerActiveAttribute, state);
            });
        }
    };

    // reset checkboxes and triggers by event
    window.addEventListener('checkbox.trigger.reset', (e) => {
        resetAllTriggersByReference(e.detail.referenceId, e.detail.state);
    });

    if ($$checkboxTrigger.length > 0) {
        $$checkboxTrigger.forEach($checkboxTrigger => {
            const checkboxReference = $checkboxTrigger.dataset.forCheckbox;
            let triggerActiveAttribute = 'aria-pressed';

            if ($checkboxTrigger.dataset.activeAttribute !== undefined) {
                triggerActiveAttribute = $checkboxTrigger.dataset.activeAttribute;
            }

            const $checkbox = $content.querySelector(`#${checkboxReference}`); // eslint-disable-line

            $checkboxTrigger.onclick = () => {
                // trigger event before the checkbox state is changed
                window.dispatchEvent(new CustomEvent('checkbox.trigger.click', {
                    view: window,
                    bubbles: true,
                    cancelable: false,
                    detail: {
                        trigger: $checkboxTrigger,
                        checkbox: $checkbox,
                        state: $checkbox.checked
                    }
                }));

                if ($checkbox.checked === true) {
                    resetAllTriggersByReference(checkboxReference, false);
                } else {
                    $checkbox.checked = true;
                    $checkboxTrigger.setAttribute(triggerActiveAttribute, $checkbox.checked);
                }

                // trigger event after the checkbox state is changed
                window.dispatchEvent(new CustomEvent('checkbox.trigger.active', {
                    view: window,
                    bubbles: true,
                    cancelable: false,
                    detail: {
                        trigger: $checkboxTrigger,
                        checkbox: $checkbox,
                        state: $checkbox.checked
                    }
                }));
            };

            if ($checkbox) {
                $checkbox.onchange = () => {
                    $checkboxTrigger.setAttribute(triggerActiveAttribute, $checkbox.checked);
                };
            }
        });
    }
};

domready(function () {
    const initApplication = () => {
        browserCheck();
        
        window.resizeHandler.init();

        _log('CSS loaded', DEBUG);

        const $body = document.querySelector('body');

        const eventScroller = new EventScroller({
            onScroll: (position, direction) => {
                _log({ 'position': position, 'direction': direction }, DEBUG);

                checkMoodVideo(eventScroller);
                checkBackToTopButton(eventScroller, position);
            }
        });
        eventScroller.initialize();

        const $searches = $body.querySelectorAll('[data-search="root"]');
        if ($searches.length > 0) {

            for (let i = 0; i < $searches.length; i++) {
                let $search = $searches[i];

                new Search($search, {
                    breakpoints: window.OPTIONS.breakpoints
                });
            }
        }

        const $languageSwitch = document.querySelector('[data-language]');
        if ($languageSwitch) {

            $languageSwitch.addEventListener('change', () => {
                document.location.href = $languageSwitch.value;
            });
        }

        const $optOutLink = document.getElementById('ga-opt-out');
        if ($optOutLink) {
            $optOutLink.addEventListener('click', (e) => {
                e.preventDefault();
                gaOptout();
            });
        }

        initContent($body, OPTIONS);

        window.setTimeout(() => {
            window.checkInview();
        }, 750);

        const contentUpdateEvent = new CustomEvent('content.loaded', {
            view: window,
            bubbles: true,
            cancelable: false,
            detail: {
                $context: document
            }
        });

        window.addEventListener('content.loaded', (e) => {
            initDataForCheckboxes(e.detail.$context);
        });

        window.setTimeout(() => {
            window.dispatchEvent(contentUpdateEvent);
        }, 500);

        redirectAnchors(() => {
            initScrollToAnchor();
        });
    };

    initApplication();

});